import { Filter } from "@/shared/lib/filter/filter";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { backendApi } from "@/services/backend/api";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { Lst } from "@/shared/lib/lst/lst";

const vbsWorkOrdersApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsWorkOrderCreate: build.mutation<WorkOrder, CreateRequest>({
      query: (request) => ({
        url: `/vbs/work-order`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
      ],
    }),
    vbsWorkOrderShow: build.query<WorkOrder, ShowRequest>({
      query: (request) => ({
        url: `/vbs/work-order`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsWorkOrder", id: result.id }] : [],
    }),
    vbsWorkOrderList: build.query<Lst<WorkOrder>, ListRequest>({
      query: (request) => ({
        url: `/vbs/work-order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsWorkOrder", id: "LIST" }] : [],
    }),
    vbsWorkOrderUpdateState: build.mutation<WorkOrder, UpdateStateRequest>({
      query: (request) => ({
        url: `/vbs/work-order/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateExecutingBranch: build.mutation<
      WorkOrder,
      UpdateExecutingBranchRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/executing-branch`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateBeginDate: build.mutation<
      WorkOrder,
      UpdateBeginDateRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/begin-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateEndDate: build.mutation<WorkOrder, UpdateEndDateRequest>({
      query: (request) => ({
        url: `/vbs/work-order/end-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateTeamLeader: build.mutation<
      WorkOrder,
      UpdateTeamLeaderRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/team-leader`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateCustomerReference: build.mutation<
      WorkOrder,
      UpdateCustomerReferenceRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/customer-reference`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
      ],
    }),
    vbsWorkOrderUpdateInternalComment: build.mutation<
      WorkOrder,
      UpdateInternalCommentRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/internal-comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateServiceAddress: build.mutation<
      WorkOrder,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateServiceContact: build.mutation<
      WorkOrder,
      UpdateServiceContactRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/service-contact`,
        method: "PATCH",
        body: {
          id: request.id,
          serviceContact: request.contact,
        },
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderUpdateRepresentative: build.mutation<
      WorkOrder,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderSign: build.mutation<WorkOrder, SignRequest>({
      query: (request) => {
        const body = new FormData();
        body.append("file", request.signature);
        return {
          url: `/vbs/work-order/sign`,
          method: "PATCH",
          headers: {
            //  this is needed, otherwise a 415 error is returned
            Accept: "application/json",
          },
          params: {
            id: request.id,
            dataFormat: request.dataFormat,
            nameOfSigner: request.nameOfSigner,
          },
          body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
      ],
    }),
    vbsWorkOrderDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/vbs/work-order`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderSetImmutable: build.mutation<WorkOrder, SetImmutableRequest>({
      query: (request) => ({
        url: `/vbs/work-order/immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsWorkOrderDelete: build.mutation<unknown, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/work-order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsWorkOrder", id: request.id },
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
  }),
});

export const {
  useVbsWorkOrderCreateMutation,
  useVbsWorkOrderShowQuery,
  useVbsWorkOrderListQuery,
  useVbsWorkOrderUpdateStateMutation,
  useVbsWorkOrderUpdateExecutingBranchMutation,
  useVbsWorkOrderUpdateBeginDateMutation,
  useVbsWorkOrderUpdateEndDateMutation,
  useVbsWorkOrderUpdateTeamLeaderMutation,
  useVbsWorkOrderUpdateCustomerReferenceMutation,
  useVbsWorkOrderUpdateInternalCommentMutation,
  useVbsWorkOrderUpdateServiceAddressMutation,
  useVbsWorkOrderUpdateServiceContactMutation,
  useVbsWorkOrderUpdateRepresentativeMutation,
  useVbsWorkOrderSignMutation,
  useVbsWorkOrderPreviewPDFQuery,
  useVbsWorkOrderDownloadPDFMutation,
  useVbsWorkOrderSetImmutableMutation,
  useVbsWorkOrderDeleteMutation,
} = vbsWorkOrdersApi;

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<WorkOrderOrderBy>[];
  state?: Filter<FulfillmentState>;
  executingBranch?: Filter<string>;
  beginDate?: Filter<string>;
  endDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export type WorkOrderOrderBy =
  | "beginDate"
  | "endDate"
  | "immutabilitySetAt"
  | "percentageCompleted"
  | "state"
  | "teamLeader"
  | "createdAt"
  | "updatedAt";

export interface UpdateStateRequest {
  id: string;
  state: FulfillmentState;
}

export interface UpdateExecutingBranchRequest {
  id: string;
  executingBranchId: string;
}

export interface UpdateBeginDateRequest {
  id: string;
  beginDate: string;
}

export interface UpdateEndDateRequest {
  id: string;
  endDate: string;
}

export interface UpdateTeamLeaderRequest {
  id: string;
  teamLeaderId: string;
}

export interface UpdateCustomerReferenceRequest {
  id: string;
  customerReference: string;
}

export interface UpdateInternalCommentRequest {
  id: string;
  internalComment: string;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateServiceContactRequest {
  id: string;
  // naming is contact and not serviceContact because
  // with "contact" the ContactCardWithMutation component
  // can be used. The conflict with the expected naming
  // "serviceContact" in the backend is resolved in the
  // mutation definition above.
  contact: Contact;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: WorkOrderDocument;
}

export enum WorkOrderDocument {
  All = "all",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  WorkOrder = "work_order",
  LastMinuteRiskAnalysis = "lmra",

  Documentation = "documentation",
}

export interface SetImmutableRequest {
  id: string;
  userId: string;
  reason: string;
}

export interface DeleteRequest {
  id: string;
}
